// =============================================================================
// VINUM - Spinner / Loading Components
// Migrated from Flutter PremiumLoadingSpinner with EXACT styling
// =============================================================================

import React from 'react';
import { cn } from '@/lib/cn';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'current';
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', color = 'primary', className, ...props }, ref) => {
    const sizeStyles = {
      sm: 'w-4 h-4 border-2',
      md: 'w-8 h-8 border-2',
      lg: 'w-12 h-12 border-3',
      xl: 'w-16 h-16 border-4',
    };

    const colorStyles = {
      primary: 'border-gray-200 border-t-primary dark:border-gray-700',
      white: 'border-white/30 border-t-white',
      current: 'border-current/30 border-t-current',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'animate-spin rounded-full',
          sizeStyles[size],
          colorStyles[color],
          className
        )}
        {...props}
      />
    );
  }
);

Spinner.displayName = 'Spinner';

// =============================================================================
// Loading Overlay
// =============================================================================

export interface LoadingOverlayProps {
  visible?: boolean;
  message?: string;
  fullScreen?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  visible = true,
  message,
  fullScreen = false,
}) => {
  if (!visible) return null;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50',
        fullScreen ? 'fixed inset-0' : 'absolute inset-0'
      )}
    >
      <Spinner size="lg" />
      {message && (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{message}</p>
      )}
    </div>
  );
};

// =============================================================================
// Page Loading
// =============================================================================

export const PageLoading: React.FC<{ message?: string }> = ({ message }) => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center py-12">
      <Spinner size="lg" />
      {message && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{message}</p>
      )}
    </div>
  );
};

// =============================================================================
// Skeleton Loader
// Migrated from Flutter SkeletonLoader
// =============================================================================

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ width, height, rounded = 'md', className, style, ...props }, ref) => {
    const roundedStyles = {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'animate-pulse bg-gray-200 dark:bg-gray-700',
          roundedStyles[rounded],
          className
        )}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          ...style,
        }}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

// Skeleton variants
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className,
}) => (
  <div className={cn('space-y-2', className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        height={16}
        width={i === lines - 1 ? '60%' : '100%'}
      />
    ))}
  </div>
);

export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('p-4 border border-border dark:border-dark-border rounded-md', className)}>
    <Skeleton height={120} className="mb-4" />
    <Skeleton height={20} width="70%" className="mb-2" />
    <Skeleton height={16} width="40%" />
  </div>
);
